<template>
  <div>
    <!-- Search and Filter Section -->
    <div class="filter-section">
      <a-row :gutter="16">
        <a-col :span="6">
          <p style="margin: 0px">Search</p>
          <a-input
            v-model="search"
            placeholder="Search by disbursement ID, factoring request ID, or seller name"
            style="width: 100%"
            allow-clear
            prefix-icon="search"
          />
        </a-col>
        <a-col :span="6">
          <p style="margin: 0px">Status</p>
          <a-select
            v-model="filters.status"
            placeholder="Filter by Status"
            allowClear
            style="width: 100%"
          >
            <a-select-option value="success">Success</a-select-option>
            <a-select-option value="pending">Pending</a-select-option>
            <a-select-option value="failed">Failed</a-select-option>
          </a-select>
        </a-col>
        <a-col :span="6">
          <p style="margin: 0px">Date Range</p>
          <a-range-picker
            v-model="filters.dateRange"
            placeholder="['Start Date', 'End Date']"
            style="width: 100%"
            format="YYYY-MM-DD"
            :allowClear="true"
          />
        </a-col>
      </a-row>
    </div>

    <!-- Disbursements Table -->
    <div class="table-section">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
        <h3 style="margin: 0;">Disbursements Overview</h3>
        <a-button
          type="primary"
          icon="plus"
          style="background: #26a246; border-color: #26a246; border-radius: 6px;"
          @click="openNewDisbursementDrawer"
        >
          New Disbursement Request
        </a-button>
      </div>
      <a-table
        :columns="disbursementColumns"
        :data-source="disbursements"
        row-key="disbursementId"
        :pagination="tablePagination"
        :scroll="{ x: 'max-content' }"
        :loading="tableLoading"
        style="background-color: #fafafa !important"
        :rowClassName="() => 'custom-table-row'"
        @change="handleTableChange"
      >
        <template slot="status" slot-scope="text">
          <StatusTag :status="text" />
        </template>
        <template slot="actions" slot-scope="text, record">
          <a-button type="link" @click="viewDisbursement(record)">
            <a-icon type="eye" style="color: #9e9e9e" />
          </a-button>
        </template>
      </a-table>
    </div>

    <!-- New Disbursement Request Component -->
    <NewDisbursementRequest
      :visible="showNewDisbursementDrawer"
      :sellerId="sellerId"
      @close="closeNewDisbursementDrawer"
      @success="onDisbursementSuccess"
    />

    <!-- Disbursement Details Drawer -->
    <DisbursementDetailsDrawer
      :visible="showDetailsDrawer"
      :disbursementData="selectedDisbursement"
      @close="closeDetailsDrawer"
    />
  </div>
</template>

<script>
import NewDisbursementRequest from "../../financing/NewDisbursementRequest.vue";
import DisbursementDetailsDrawer from "../../financing/DisbursementDetailsDrawer.vue";
import FinancingAndPaymentsService from "@/api/financing-and-payments.service.js";
import StatusTag from "../../shared-components/StatusTag.vue";
import { formatAmount, formatDate } from "@/utils";

export default {
  name: "Disbursements",
  components: {
    NewDisbursementRequest,
    DisbursementDetailsDrawer,
    StatusTag,
  },
  props: {
    sellerId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      // Search and filters
      search: "",
      searchDebounceTimer: null,
      filters: {
        status: undefined,
        dateRange: null,
      },

      // Pagination
      pagination: {
        pageIndex: 1,
        pageSize: 10,
        totalCount: 0,
      },

      // Table data and loading
      disbursements: [],
      tableLoading: false,

      // UI state
      showNewDisbursementDrawer: false,
      showDetailsDrawer: false,
      selectedDisbursement: null,

      disbursementColumns: [
        {
          title: "Disbursement ID",
          dataIndex: "disbursementId",
          key: "disbursementId",
          width: 150,
        },
        {
          title: "Factoring Request ID",
          dataIndex: "factoringRequestId",
          key: "factoringRequestId",
          width: 150,
        },
        {
          title: "Amount",
          dataIndex: "amount",
          key: "amount",
          customRender: (text) => {
            return formatAmount(text);
          },
          width: 150,
        },
        {
          title: "Status",
          dataIndex: "status",
          key: "status",
          scopedSlots: { customRender: "status" },
          width: 120,
        },
        {
          title: "Created On",
          dataIndex: "createdOn",
          key: "createdOn",
          width: 120,
        },
        {
          title: "Sent on",
          dataIndex: "sentOn",
          key: "sentOn",
          width: 120,
        },
        {
          title: "Action",
          key: "actions",
          scopedSlots: { customRender: "actions" },
          width: 80,
        },
      ],
    };
  },
  computed: {
    tablePagination() {
      return {
        current: this.pagination.pageIndex,
        pageSize: this.pagination.pageSize,
        total: this.pagination.totalCount,
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} of ${total} items`,
        showSizeChanger: false,
      };
    },
  },
  mounted() {
    this.fetchData();
  },
  watch: {
    "pagination.pageIndex"() {
      this.fetchData();
    },
    search() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.pagination.pageIndex = 1;
        this.fetchData();
      }, 400);
    },
    filters: {
      handler() {
        this.pagination.pageIndex = 1;
        this.fetchData();
      },
      deep: true,
    },
  },
  methods: {
    formatAmount,
    formatDate,
    async fetchData() {
      this.tableLoading = true;

      try {
        // Convert dateRange to startDate and endDate
        let startDate = "";
        let endDate = "";

        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          // Create Date objects with time 00:00:00 for start date
          const startDateObj = new Date(
            this.filters.dateRange[0].format("YYYY-MM-DD")
          );
          startDateObj.setHours(0, 0, 0, 0);
          startDate = startDateObj;

          // Create Date objects with time 23:59:59 for end date
          const endDateObj = new Date(
            this.filters.dateRange[1].format("YYYY-MM-DD")
          );
          endDateObj.setHours(23, 59, 59, 999);
          endDate = endDateObj;
        }

        const params = {
          pageIndex: this.pagination.pageIndex,
          pageSize: this.pagination.pageSize,
          status: this.filters.status || "",
          search: this.search || "",
          startDate: startDate,
          endDate: endDate,
          sellerId: this.sellerId, // Pass sellerId as parameter
        };

        const response = await FinancingAndPaymentsService.getDisbursements(
          params
        );

        if (response.data?.code === 200) {
          const data = response.data.data;
          this.disbursements = (data.results || []).map((item) => ({
            disbursementId: item.id || "N/A",
            factoringRequestId: item.factoringId || "N/A",
            amount: item.amount || 0,
            sellerName: item.invoice.sellerName || "N/A",
            status: item.status || "Unknown",
            terraResponseMessage: item.terraResponseMessage || null,
            createdOn: item.createdAt ? formatDate(item.createdAt) : "N/A",
            sentOn: item.completedAt ? formatDate(item.completedAt) : "N/A",
          }));
          this.pagination.totalCount = data.totalCount || 0;
        } else {
          this.disbursements = [];
          this.pagination.totalCount = 0;
          if (response.data?.message) {
            this.$message.warning(response.data.message);
          }
        }
      } catch (error) {
        console.error("Error fetching disbursements:", error);
        this.disbursements = [];
        this.pagination.totalCount = 0;
        this.$message.error("Failed to fetch disbursements. Please try again.");
      } finally {
        this.tableLoading = false;
      }
    },

    handleTableChange(pagination) {
      if (pagination.current !== this.pagination.pageIndex) {
        this.pagination.pageIndex = pagination.current;
      }
    },

    clearFilters() {
      this.filters = {
        status: undefined,
        dateRange: null,
      };
      this.search = "";
    },

    viewDisbursement(record) {
      this.selectedDisbursement = record;
      this.showDetailsDrawer = true;
    },

    openNewDisbursementDrawer() {
      this.showNewDisbursementDrawer = true;
    },

    closeNewDisbursementDrawer() {
      this.showNewDisbursementDrawer = false;
    },

    closeDetailsDrawer() {
      this.showDetailsDrawer = false;
      this.selectedDisbursement = null;
    },

    async onDisbursementSuccess() {
      await this.fetchData();
    },
    getExportData() {
      // Return formatted data for Excel export
      return this.disbursements.map(disbursement => ({
        "Disbursement ID": disbursement.disbursementId,
        "Factoring Request ID": disbursement.factoringRequestId,
        "Amount": disbursement.amount,
        "Status": disbursement.status,
        "Created On": disbursement.createdOn,
        "Sent On": disbursement.sentOn
      }));
    },
  },
};
</script>

<style scoped>
/* Standard filter section styling */
.filter-section {
  background: #fff;
  padding: 24px;
  border-radius: 8px;
  margin-bottom: 24px;
  border-bottom: 1px solid #e5e7eb;
}

.filter-section .ant-row {
  margin-bottom: 0;
}

.filter-section .ant-col {
  margin-bottom: 16px;
}

.filter-section .ant-col:last-child {
  margin-bottom: 0;
}

a-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #374151;
}

.table-section {
  background: #fff;
  padding: 24px;
  border-radius: 8px;
}

.table-section h3 {
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}

/* Responsive */
@media (max-width: 768px) {
  .filter-section .ant-col {
    margin-bottom: 16px;
  }
}
</style>
